import { uniqueId } from "lodash"
import NotificationService from "@/services/NotificationService"
import PrivilegeService from "@/services/PrivilegeService"

// Load default storage values from app.config file
import config from "@/app.config"
import { getTimeZoneName } from "@/utils/datetime"

const module = {
  namespaced: true,
  state: {
    locale: config.plugins.i18n.locale,
    isThemeDark: config.plugins.vuetify.theme.dark,
    languages: [
      // { language: "Deutsch", short: "de" },
      // { language: "English", short: "en" },
      // { language: "Español", short: "es" },
      { language: "Français", short: "fr" },
      // { language: "Italiano", short: "it" },
      // { language: "中文", short: "cn" }
    ],
    errors: [],
    isOnline: true,
    user: null,
    project: null,
    department: null,
    company: null,
    loading: {
      request: false
    },
    notifications: [],
    userPrivileges: null,
  },
  actions: {
    async fetchNotifications(context) {
      return NotificationService.list(null).then(r => {
        context.commit("setNotifications", r || [])
      })
    },
    async updateNotifications(context) {
      return NotificationService.markRead().then(r => {
        context.commit("setNotifications", r || [])
      })
    },
    async loadUserPrivileges(context) {
      const userId = context.state.user?.user?.pk || context.state.user?.user?.id
      if (!userId) {
        context.commit("setUserPrivileges", [])
        return []
      }
      try {
        const data = await PrivilegeService.getUserProjectPrivileges(userId)
        const list = Array.isArray(data) ? data : (data?.results || [])
        context.commit("setUserPrivileges", list)
        return list
      } catch (e) {
        context.commit("setUserPrivileges", [])
        return []
      }
    }
  },
  getters: {
    getCompany(state) {
      return state.company ? state.company : -1
    },
    getLocale(state) {
      return state.locale
    },
    getNewNotifications(state) {
      return state.notifications.filter(e => !e.read)
    },
    isUserAdmin(state) {
      return state.user?.admin
    },
    isCompanyOwner(state) {
      return state.user?.is_company_owner
    },
    isSignedIn(state) {
      return state.user != null
    },
    canCreateProject(state) {
      return state.user?.can_create_project
    },
    hasSelectedProject(state) {
      return !!state.project
    },
    hasSelectedDepartment(state) {
      return !!state.department
    },
    isUserStaff(state) {
      return state.user?.is_staff
    },
    isUserSuperUser(state) {
      return state.user?.is_superuser
    },
    isUserPro(state) {
      return state.user?.is_pro_mobile
    },
    userPrivilegeCodes(state) {
      const list = state.userPrivileges || []
      const codes = new Set()
      list.forEach(p => {
        const actions = p?.actions || p?.action || []
        const arr = Array.isArray(actions) ? actions : [actions]
        arr.forEach(a => {
          const code = a?.permission_code || a
          if (code) codes.add(code)
        })
      })
      return codes
    },
    pwaCapabilities(state, getters) {
      // Pompiste : ne voit QUE les transactions (historique)
      if (state.user?.is_pro_mobile) {
        return {
          isPompiste: true,
          canSeeTransactions: true,
          canSeeVehicles: false,
          canSeeVouchers: false,
          canCreateVehicle: false,
          canCreateVoucher: false,
          canSeeHome: false,
        }
      }
      // Admin / Manager : tout
      const role = state.company?.company_role
      const isAdmin = role === "administrator" || role === "manager" || state.user?.is_company_owner
      if (isAdmin) {
        return {
          isPompiste: false,
          canSeeTransactions: true,
          canSeeVehicles: true,
          canSeeVouchers: true,
          canCreateVehicle: true,
          canCreateVoucher: true,
          canSeeHome: true,
        }
      }
      // Operateur : on regarde les privileges accordes
      const codes = getters.userPrivilegeCodes
      const has = c => codes.has(c)
      return {
        isPompiste: false,
        canSeeTransactions: has("show_transaction"),
        canSeeVehicles: has("show_vehicules"),
        canSeeVouchers: has("show_purchase"),
        canCreateVehicle: has("edit_vehicules"),
        canCreateVoucher: has("edit_purchase"),
        canSeeHome: true,
      }
    }
  },
  mutations: {
    clearSession(state) {
      Object.assign(state, {
        errors: [],
        isOnline: true,
        user: null,
        project: null,
        department: null,
        company: null,
        loading: {
          request: false
        },
        notifications: [],
        userPrivileges: null,
      })
    },
    setUserPrivileges(state, list) {
      state.userPrivileges = list || []
    },
    setLocale(state, newValue) {
      state.locale = newValue
    },
    setIsThemeDark(state, newValue) {
      state.isThemeDark = newValue
    },
    setUser(state, newValue) {
      state.user = { ...newValue, tzName: getTimeZoneName() }
    },
    setProject(state, newValue) {
      state.project = newValue
    },
    setDepartment(state, newValue) {
      state.department = newValue
    },
    setCompany(state, newValue) {
      console.log("setCompany called with:", newValue);
      state.company = newValue
      
      console.log("state.company after assignment:", state.company);
    },
    clearProject(state) {
      state.project = null
    },
    clearDepartment(state) {
      state.department = null
    },
    lockExtraFields(state) {
      if (state.project) {
        state.project.extra_fields_locked = true
      }
    },
    setNotifications(state, newValue) {
      state.notifications = newValue
    },
    clearMessages(state) {
      state.errors = []
    },
    message(state, ml) {
      if (ml)
        state.errors.push({
          id: uniqueId(),
          text: ml.msg ?? "No message notification",
          color: ml.color,
          isVisible: true,
          createdAt: new Date().getTime(),
          timeout: ml.timeout ?? 3000
        })
    },
    success(state, msg) {
      state.errors.push({
        id: uniqueId(),
        text: msg,
        color: "success",
        isVisible: true,
        createdAt: new Date().getTime(),
        timeout: 3000
      })
    },
    online(state) {
      state.isOnline = true
    },
    offline(state) {
      state.isOnline = false
    },
    "loading-request-start": state => {
      state.loading.request = true
    },
    "loading-request-end": state => {
      state.loading.request = false
    }
  }
}

export default module
